import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import { Description } from 'components/v2/confirm-dialog/styled';
import InputFormField from 'components/v2/form/InputFormField';
import { HelpBox } from 'components/v2/help-boxes';
import { toaster } from 'components/v2/toast';
import { passwordValidationSchema } from 'constants/userValidationSchemes';
import { Form, Formik, FormikHelpers } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import AuthService from 'services/AuthService';
import { ChangePasswordModel } from 'services/EpdClient';
import styled from 'styled-components';
import { HeaderDialog } from 'styles/Styles.styled';
import * as Yup from 'yup';
import DialogClearStyles, { DialogClearStylesButtonsFooter } from './DialogClearStyles';

interface PasswordChangeFormProps {
  isOpened: boolean;
  onHide: () => void;
}

const ChangePasswordDialog: React.FC<PasswordChangeFormProps> = ({ isOpened, onHide }) => {
  const { t } = useTranslation();

  const validationSchema = Yup.object().shape({
    password: Yup.string().required(t('account.validation.passwordRequired')),
    // Validate new password not same as password only if the current password specified.
    newPassword: passwordValidationSchema(t)
      .when('password', {
        is: (value: string) => value?.trim() !== '',
        then: (schema: Yup.StringSchema) =>
          schema.test('not-same-as-password', t('account.validation.newPasswordNotSame'), function (value) {
            const { password } = this.parent;
            return !password || !value || password !== value;
          }),
      })
      .required(t('account.validation.newPasswordRequired')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], t('account.validation.passwordsNotMatch'))
      .required(t('account.validation.confirmPasswordRequired')),
  });

  const initialValues: ChangePasswordModel = {
    password: '',
    newPassword: '',
    confirmPassword: '',
  };

  const handleSubmit = async (values: ChangePasswordModel, { setStatus }: FormikHelpers<ChangePasswordModel>) => {
    setStatus({});
    try {
      await AuthService.changePassword(values);
      toaster({
        severity: 'success',
        summary: 'Account information updated successfully!',
      });
      onHide();
    } catch (error: any) {
      if (error?.detail?.includes('INCORRECT_PWD')) {
        // We should not close  dialog in this case, because user should be able to correct the password.
        // We should show the error message in the input field.
        const errorMessage = 'Incorrect current password';
        setStatus({ errorMessage });
        return;
      }
      toaster({
        severity: 'error',
        summary: error?.detail || 'Unable to update account information due to unexpected error',
        details: error?.detail ? '' : 'Please try again later or contact support.',
      });
      onHide();
    }
  };

  return (
    <DialogClearStyles
      visible={isOpened}
      onHide={() => onHide()}
      header={<HeaderDialog>Change password</HeaderDialog>}
      closable={false}
      draggable={false}
    >
      <Formik initialStatus={{}} initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ handleSubmit, isValid, errors, touched, dirty, isSubmitting, status }) => (
          <DialogContent as={Form}>
            <Description>
              <HelpBox>
                Please set the new password using small and capital letters, numbers and special symbols.
                <br />
                The minimal password length should be 6 characters.
              </HelpBox>
            </Description>

            <InputsContainer>
              <InputFormField
                type="password"
                label="Current Password"
                name="password"
                errorPositioning="absolute"
                error={status.errorMessage}
              />
              <InputFormField type="password" label="New Password" name="newPassword" errorPositioning="absolute" />
              <InputFormField
                type="password"
                label="Confirm New Password"
                name="confirmPassword"
                errorPositioning="absolute"
              />
            </InputsContainer>

            <DialogFooter>
              <DialogClearStylesButtonsFooter>
                <SecondaryButton type="button" label="Cancel" onClick={() => onHide()} />
                <PrimaryButton
                  type="submit"
                  label={isSubmitting ? 'Saving...' : 'Save'}
                  onClick={() => handleSubmit()}
                  disabled={(!isValid && Object.keys(errors).some((error) => error in touched)) || !dirty || isSubmitting}
                />
              </DialogClearStylesButtonsFooter>
            </DialogFooter>
          </DialogContent>
        )}
      </Formik>
    </DialogClearStyles>
  );
};

const DialogContent = styled.div`
  width: 90vw;
  max-width: 560px;
  padding-left: 30px;
  padding-right: 30px;
  padding-bottom: 30px;
`;

const InputsContainer = styled.div`
  margin-top: 30px;
  display: grid;
  gap: 30px;
`;

const DialogFooter = styled.div`
  margin-top: 30px;
`;

export default ChangePasswordDialog;
